import { Button, Group, Title } from "@mantine/core";
import { OnboardingStage } from "./OnboardingStage";
import { ArrowLeftIcon, ArrowRightIcon } from "./onboardingIcons";
import { ARTIST_INTENTS, useIntentSelection } from "./intentOptions";

const CONTROL_HEIGHT = 48;

export interface IntentScreenUser {
  Email?: string;
  email?: string;
}

export interface IntentScreenProps {
  user?: IntentScreenUser | null;
  isArtistReferral?: boolean;
  onContinue: (intent: number) => void;
  onScrollTop?: () => void;
  onBack?: () => void;
}

export function IntentScreen({
  user,
  isArtistReferral,
  onContinue,
  onScrollTop,
  onBack,
}: IntentScreenProps) {
  const { intent, error, toggle, submit } = useIntentSelection({
    options: ARTIST_INTENTS,
    isArtistReferral,
    onContinue,
    onScrollTop,
  });

  const signedInAs = user && (user.Email || user.email) ? user.Email || user.email : undefined;

  return (
    <OnboardingStage activeStep={2} signedInAs={signedInAs}>
      <Title order={1} fz="30px" fw={500} data-testid="screen-heading" style={{ letterSpacing: "-0.01em", margin: "0 0 6px" }}>
        What do you want to do on ArtCloud?
      </Title>
      <p style={{ color: "var(--mantine-color-gray-6)", fontSize: 15, margin: "0 0 24px" }}>
        We'll personalise your Getting Started checklist around these.
      </p>

      <div className="intent-chips" style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {ARTIST_INTENTS.map((p) => {
          const selected = (intent & p.value) !== 0;
          return (
            <button
              key={p.value}
              type="button"
              aria-pressed={selected}
              data-testid={`intent-chip-${p.value}`}
              data-selected={selected ? "true" : "false"}
              onClick={() => toggle(p.value)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "12px 18px",
                borderRadius: 999,
                border: `1px solid ${selected ? "#00AEFF" : "#e3e1de"}`,
                background: selected ? "rgba(0, 177, 255, 0.10)" : "#f5f3f0",
                color: "#1a1a1a",
                fontSize: 15.5,
                fontWeight: 500,
                lineHeight: 1,
                cursor: "pointer",
                transition: "border-color 140ms ease, background 140ms ease",
              }}
            >
              {p.label}
            </button>
          );
        })}
      </div>

      {error && (
        <p role="alert" style={{ color: "#c1352b", fontSize: 14, margin: "14px 0 0" }}>
          Please select at least one option to continue
        </p>
      )}

      {/* Card foot: back (left) · primary (right) — mirrors the Profile screen */}
      <Group justify="space-between" mt="xl" wrap="nowrap">
        {onBack ? (
          <Button
            type="button"
            variant="default"
            h={CONTROL_HEIGHT}
            px="md"
            fw={500}
            onClick={onBack}
            data-testid="back-button"
            aria-label="Back"
          >
            <ArrowLeftIcon />
          </Button>
        ) : (
          <span />
        )}
        <Button
          type="button"
          h={CONTROL_HEIGHT}
          px="xl"
          fw={500}
          rightSection={<ArrowRightIcon />}
          onClick={submit}
          data-testid="continue-button"
        >
          Continue
        </Button>
      </Group>
    </OnboardingStage>
  );
}
